import type { ConfiguratorPreviewContext } from '../preview-context';

/**
 * Mount-time configuration a webshop plugin or theme passes to the
 * bundle. Lives on `ConfiguratorInstance.config` as a frozen read-only
 * record — once the instance is registered, nothing on this object
 * changes for its lifetime (host code that wants to react to a config
 * change should re-mount).
 *
 * Sources, in priority order:
 *
 *   1. `data-configento-config` attribute on the mount element — a
 *      JSON-encoded object. The most common case for shop plugins
 *      that emit one script-tag-per-product.
 *   2. Individual `data-configento-{key}` attributes on the mount
 *      element. Flatter and easier to write in static HTML.
 *   3. `window._configentoPreview` — the admin-preview iframe path.
 *      Mostly here because the admin already drives this shape; new
 *      plugins should use the data-attribute mechanism.
 *
 * Every field is optional except `componentId`, which the bundle
 * needs to know which Component to fetch. A missing componentId on
 * the resolved config falls back to `data-component-id`; if neither
 * is set the bundle warns and mounts a placeholder.
 */
export interface WebshopConfig {
    /** Required: which Component to load. */
    componentId: string;
    /** Customer identifier — usually the customer_no for cloud calls. */
    customer?: string;
    /** Cloud auth token (per-customer Basic-Auth or similar). */
    token?: string;
    /** Optional URL the bundle fetches to mint a fresh token on 401 retry —
     *  storefront-plugin path. See {@link resolveToken}. */
    tokenEndpoint?: string;
    /** Cloud base URL. Defaults to whatever the bundle was built against. */
    cloudBaseUrl?: string;
    /** Host shop's effective tax rate, used to convert configurator-base
     *  prices into the customer-facing display currency basis. */
    taxRate?: number | string;
    /** Locale of the host-shop UI strings ("Total", "Add to cart", …). */
    uiLocale?: string;
    /** Override for which `title[locale]` to pick from component data.
     *  When omitted, falls back to `component.defaultLocale`. */
    contentLocale?: string;
    /** ISO currency code (e.g. `"EUR"`). When omitted the bundle picks
     *  whatever the component data carries. */
    currency?: string;
    /**
     * Factors from a currency into {@link currency}, keyed by ISO code — the
     * shop's own exchange rates (`{"EUR": 0.94}` in a CHF shop). With a rate
     * for the Component's currency every displayed and reported amount is
     * converted; without one, prices stay in the Component's currency.
     * Attribute form: `data-configento-currency-rates='{"EUR":0.94}'`.
     */
    currencyRates?: Record<string, number>;
    /** Frontend version tag — usually "v3"; rarely overridden. */
    frontendVersion?: string;
    /** Base URL for the V3 admin's storefront-asset endpoint
     *  (`POST /api/v1/storefront/assets` + `GET /api/v1/storefront/assets/{id}`),
     *  used by `inst.storeAsset()` and `Configento.loadAsset(id)`. Plugins
     *  (Magento, Shopware) inject this; default is `admin.configento.app`.
     *  Decoupled from `cloudBaseUrl` (V3 cloud / Java backend) — they are
     *  different services. Leave unset to use the default. */
    assetBaseUrl?: string;

    /**
     * Show a language chooser before the configurator itself.
     *
     * Off by default. A merchant whose Component carries several languages
     * sets `data-configento-language-select` on the mount element; the bundle
     * then asks once, in flags and native names, and starts the configurator
     * in the answer. The choice is remembered for the browsing session, so
     * walking back to the product page does not ask again.
     *
     * The gate stays out of the way when it would be pointless: a Component
     * with one language renders straight through, as does a reload where the
     * visitor already chose.
     */
    languageSelect?: boolean;

    /**
     * Free text above the flags of the language gate ("Choose your
     * language"). Shown as written, in no particular language — the visitor
     * has not picked one yet, so a merchant serving several markets writes
     * it in all of them. Empty or absent: no heading.
     */
    languageSelectTitle?: string;

    /**
     * Which languages the gate offers, as locale codes
     * (`"de_DE,en_US,fr_FR"` on the attribute). Order is honoured.
     *
     * Omit it and the bundle derives the list from the Component itself —
     * which is the better default, because the list then cannot drift away
     * from what the merchant actually translated. Set it to narrow a
     * Component down to the languages one particular shop sells in.
     */
    languages?: string[];

    /**
     * Attribute values of the shop product the configurator sits on, keyed
     * by attribute code (`{ "weight": "12.5" }`). Feeds `productattribute`
     * characteristics. Shop plugins pass it in `data-configento-config`.
     */
    productAttributes?: Record<string, string>;

    /**
     * Shop content the component's HTML can pull in with
     * `$content[identifier]`, keyed by identifier, values base64-encoded
     * (see parse/contentBlocks.ts). Shop plugins pass it in
     * `data-configento-config`; the Magento plugin uses the preview context.
     */
    cmsBlocks?: Record<string, string>;

    /**
     * How much chrome the bundle renders around the value inputs.
     *
     * `admin-preview` (the default) renders everything: the running total,
     * the summary, the combined image. That is what a plain snippet on a CMS
     * page wants — one element in, a whole configurator out.
     *
     * `storefront` strips the total and the summary because the host shop has
     * its own price box and its own summary, and rendering ours would double
     * them up. Magento, Shopware and Woo set this.
     *
     * Was reachable only through `window._configentoPreview` before, which
     * made it invisible to anyone integrating by data attributes.
     */
    embedMode?: 'admin-preview' | 'storefront';

    /**
     * CSS selector for a host-page element that should receive the running
     * total instead of the built-in bottom bar.
     *
     * The successor to legacy `<div id="configento-price">`, which
     * `html5.js:setPrice()` filled whenever it happened to exist. A selector
     * rather than a fixed id, so two configurators on one page can each have
     * their own target.
     */
    priceMount?: string;

    /**
     * CSS selector for a host-page element that should receive the
     * per-Characteristic summary instead of the built-in panel. No legacy
     * equivalent — the old HTML5 snippet had no summary container at all.
     */
    summaryMount?: string;

    /**
     * Whether the summary starts expanded. Collapsed by default: under a long
     * form, a panel that opens itself pushes the total off screen. A summary
     * given its own place on the page via {@link summaryMount} usually wants
     * the opposite, with nothing to click first.
     *
     * Initial state only — once the customer collapses it, it stays that way.
     */
    summaryOpen?: boolean;

    /**
     * CSS selector for a host-page element that should receive the combined
     * product image. Storefront plugins point this at their gallery
     * placeholder (Magento default `div.gallery-placeholder`) so the
     * configurator-driven image replaces the static product photo.
     *
     * The successor to legacy `_configentoSettings['_cssPathCombinedImage']`.
     */
    combinedImageMount?: string;

    /**
     * Height the mount element keeps free while the Component loads (any CSS
     * length, e.g. `"640px"`). Without it the page below the configurator
     * jumps once the first step renders. Dropped again after the first render
     * unless {@link minHeightKeep} is set.
     */
    minHeight?: string;

    /** Keep {@link minHeight} on the mount element after the first render. */
    minHeightKeep?: boolean;

    /**
     * How much of the built-in wizard navigation renders: `full` (tabs,
     * progress, Back/Next — the default), `buttons` (Back/Next only) or `none`
     * (the host drives the steps through the instance API). Overrides the
     * Component's own setting.
     */
    wizardNav?: WizardNav;

    /**
     * The Component itself, exactly as `GET /api/v3/components/{id}` returns
     * it. Set it and the bundle renders without fetching token or Component —
     * for hosts that already have the published JSON on the server. Only via
     * `data-configento-config` / `data-configento-component-json`.
     */
    component?: unknown;
}

export type WizardNav = 'full' | 'buttons' | 'none';

export function parseWizardNav(v: unknown): WizardNav | undefined {
    return v === 'full' || v === 'buttons' || v === 'none' ? v : undefined;
}

/**
 * Resolve the per-mount configuration. Reads the three documented
 * sources in priority order and falls back to sensible defaults when
 * the host omitted a field.
 *
 * Pure function — easy to test outside the browser.
 */
export function resolveWebshopConfig(
    element: HTMLElement,
    previewContext: ConfiguratorPreviewContext | undefined,
): WebshopConfig | null {
    const fromJson = readJsonConfig(element);
    const fromAttrs = readAttrConfig(element);
    const fromPreview = previewContextAsConfig(previewContext);

    const merged: Partial<WebshopConfig> = {
        ...fromPreview,
        ...fromAttrs,
        ...fromJson,
    };
    if (!merged.componentId) return null;
    return Object.freeze(merged as WebshopConfig);
}

function readJsonConfig(element: HTMLElement): Partial<WebshopConfig> {
    const raw = element.dataset.configentoConfig;
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed === null || typeof parsed !== 'object') return {};
        return parsed as Partial<WebshopConfig>;
    } catch (err) {
        console.warn('[Configento] data-configento-config is not valid JSON:', err);
        return {};
    }
}

function readAttrConfig(element: HTMLElement): Partial<WebshopConfig> {
    const ds = element.dataset;
    const out: Partial<WebshopConfig> = {};
    if (ds.componentId)     out.componentId     = ds.componentId;
    if (ds.configentoCustomer)        out.customer        = ds.configentoCustomer;
    if (ds.configentoToken)           out.token           = ds.configentoToken;
    if (ds.configentoTokenEndpoint)   out.tokenEndpoint   = ds.configentoTokenEndpoint;
    if (ds.configentoCloudBaseUrl)    out.cloudBaseUrl    = ds.configentoCloudBaseUrl;
    if (ds.configentoTaxRate)         out.taxRate         = ds.configentoTaxRate;
    if (ds.configentoLocale)          out.uiLocale        = ds.configentoLocale;
    if (ds.configentoContentLocale)   out.contentLocale   = ds.configentoContentLocale;
    if (ds.configentoCurrency)        out.currency        = ds.configentoCurrency;
    const currencyRates = readRatesAttr(ds.configentoCurrencyRates);
    if (currencyRates !== undefined) out.currencyRates = currencyRates;
    if (ds.configentoFrontendVersion) out.frontendVersion = ds.configentoFrontendVersion;
    if (ds.configentoAssetBaseUrl)    out.assetBaseUrl    = ds.configentoAssetBaseUrl;
    if (ds.configentoPriceMount)         out.priceMount         = ds.configentoPriceMount;
    if (ds.configentoSummaryMount)       out.summaryMount       = ds.configentoSummaryMount;
    const summaryOpen = readBoolAttr(ds.configentoSummaryOpen);
    if (summaryOpen !== undefined) out.summaryOpen = summaryOpen;
    if (ds.configentoCombinedImageMount) out.combinedImageMount = ds.configentoCombinedImageMount;
    const languageSelect = readBoolAttr(ds.configentoLanguageSelect);
    if (languageSelect !== undefined) out.languageSelect = languageSelect;
    if (ds.configentoLanguageSelectTitle) out.languageSelectTitle = ds.configentoLanguageSelectTitle;
    // Comma-separated, whitespace tolerated: merchants write this by hand in a
    // CMS field, and "de_DE, en_US" should mean what it looks like.
    if (ds.configentoLanguages) {
        const list = ds.configentoLanguages
            .split(',')
            .map((code) => code.trim())
            .filter((code) => code !== '');
        if (list.length > 0) out.languages = list;
    }
    // Anything other than the two known modes is ignored rather than passed
    // through — a typo should leave the default in place, not put the bundle
    // in a third state nothing handles.
    if (ds.configentoEmbedMode === 'storefront' || ds.configentoEmbedMode === 'admin-preview') {
        out.embedMode = ds.configentoEmbedMode;
    }
    if (ds.configentoMinHeight) out.minHeight = ds.configentoMinHeight;
    const minHeightKeep = readBoolAttr(ds.configentoMinHeightKeep);
    if (minHeightKeep !== undefined) out.minHeightKeep = minHeightKeep;
    const wizardNav = parseWizardNav(ds.configentoWizardNav);
    if (wizardNav) out.wizardNav = wizardNav;
    if (ds.configentoComponentJson) {
        try {
            out.component = JSON.parse(ds.configentoComponentJson) as unknown;
        } catch (err) {
            console.warn('[Configento] data-configento-component-json is not valid JSON — fetching instead:', err);
        }
    }
    return out;
}

function previewContextAsConfig(
    ctx: ConfiguratorPreviewContext | undefined,
): Partial<WebshopConfig> {
    if (!ctx) return {};
    return {
        componentId:     ctx.componentId,
        customer:        ctx.customer,
        token:           ctx.token,
        tokenEndpoint:   ctx.tokenEndpoint,
        cloudBaseUrl:    ctx.cloudBaseUrl,
        assetBaseUrl:    ctx.assetBaseUrl,
        taxRate:         ctx.taxRate,
        currency:        ctx.currency,
        currencyRates:   ctx.currencyRates,
        uiLocale:        ctx.uiLocale,
        contentLocale:   ctx.contentLocale,
        frontendVersion: ctx.frontendVersion,
        // Both of these used to be read straight off the preview context deep
        // inside ConfiguratorRoot. Routing them through the config means a
        // data attribute can supply them too, and there is one place that
        // decides what wins.
        embedMode:          ctx.embedMode,
        combinedImageMount: ctx.combinedImageMount,
        languageSelect:     ctx.languageSelect,
        languageSelectTitle: ctx.languageSelectTitle,
        languages:          ctx.languages,
        productAttributes:  ctx.productAttributes,
        cmsBlocks:          ctx.cmsBlocks,
    };
}

/**
 * Read `{"EUR": 0.94, …}` from a data attribute.
 *
 * Only positive finite numbers survive, keys are upper-cased. A malformed
 * attribute is ignored with a warning — prices then stay in the Component's
 * currency, which is correctly labelled, instead of being multiplied by junk.
 */
function readRatesAttr(raw: string | undefined): Record<string, number> | undefined {
    if (raw === undefined || raw.trim() === '') return undefined;
    try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not an object');
        const out: Record<string, number> = {};
        for (const [code, rate] of Object.entries(parsed as Record<string, unknown>)) {
            if (typeof rate === 'number' && Number.isFinite(rate) && rate > 0) out[code.toUpperCase()] = rate;
        }
        return out;
    } catch {
        console.warn(`[Configento] not a currency-rate map: "${raw}" — ignoring`);
        return undefined;
    }
}

/**
 * Read a boolean from a data attribute.
 *
 * Data attributes are strings, and `Boolean("false")` is `true` — so the
 * obvious coercion gets the one case a host is most likely to write exactly
 * backwards. Accepts what an HTML author would reasonably type, including the
 * bare attribute (`data-configento-summary-open`), which arrives as `''` and
 * reads as "on" the way `disabled` does.
 *
 * Anything unrecognised returns undefined so the default stands, rather than
 * a typo silently meaning `false`.
 */
function readBoolAttr(raw: string | undefined): boolean | undefined {
    if (raw === undefined) return undefined;

    const value = raw.trim().toLowerCase();

    if (value === '' || value === 'true' || value === '1' || value === 'yes') return true;
    if (value === 'false' || value === '0' || value === 'no') return false;

    console.warn(`[Configento] not a boolean: "${raw}" — ignoring`);
    return undefined;
}
